import * as React from 'react';

import * as Ramda from 'ramda';

import {Aside} from 'shells/aside/aside';
import {Button} from 'shells/button/button';
import {SlideshowControls} from 'shells/carousel/slideshow-controls';
import {Pillset} from 'shells/entity-pill/pillset';
import {RightFrame, ModalMain, FooterWithControls} from 'shells/modal/step-modal/layout';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {TextfieldWithApply} from 'shells/textfield/textfield-with-apply';
import {ModalBody} from 'shells/typography/modal-body';
import {ModalHeader} from 'shells/typography/modal-header';
import {ModalSubheader} from 'shells/typography/modal-subheader';
import {ModalTitle} from 'shells/typography/modal-title';

import {PinnedInstanceNote} from '../../pinned-domains/pinned-instance-note';

import {trackModalPageLoad} from '../track-page-load';

import {INITIAL_SYNC_NUM_DAYS_AGO} from './consts';
import {SyncMutations} from './sync-mutations';

import styles from './blocklist.module.css';

type Props = {
    goBack: () => void;
    onSyncSuccessful: () => void;
    blocklistedEmails: string[];
    isBlocklistLoading: boolean;
    setBlocklist: (arg1: string[]) => Promise<any>;
    syncOld: boolean;
    createPeople: boolean;
    provider: 'GOOGLE' | 'GOOGLE_APPS' | 'OFFICE365';
    currentUrl: string;
};

export function Blocklist(props: Props) {
    const [blocklist, addToBlocklist, removeFromBlocklist, setBlocklistState] = useUniqueList([]);
    const [isConnecting, setIsConnecting] = React.useState<boolean>(false);
    const {blocklistedEmails, isBlocklistLoading, setBlocklist} = props;

    React.useEffect(() => {
        if (blocklistedEmails && blocklistedEmails.length && blocklistedEmails) {
            setBlocklistState(blocklistedEmails);
        }
    }, [blocklistedEmails, setBlocklistState]);

    React.useEffect(() => {
        trackModalPageLoad(props.currentUrl);
    }, [props.currentUrl]);

    return (
        <RightFrame>
            <ModalMain fullheight={true}>
                <SpacerGroup mb={32}>
                    <ModalHeader>Do you want to block particular contacts?</ModalHeader>
                    <ModalSubheader>
                        Add email addresses to your blocklist to prevent Nutshell from ever syncing
                        in their messages.
                    </ModalSubheader>
                    <Aside>
                        <PinnedInstanceNote />
                    </Aside>
                </SpacerGroup>

                <SpacerGroup mb={12}>
                    <ModalTitle>Blocklist an email address</ModalTitle>
                    <TextfieldWithApply
                        placeholder='mom@hotmail.com'
                        disabled={isBlocklistLoading}
                        buttonText={isBlocklistLoading ? 'Loading…' : 'Add'}
                        buttonVariant='secondary'
                        // @ts-expect-error
                        onApply={addToBlocklist}
                    />
                </SpacerGroup>
                {/* @ts-expect-error */}
                <Pillset
                    emptyState={
                        <ModalBody>
                            <span className={styles['empty-state']}>
                                You haven’t blocked any email addresses yet
                            </span>
                        </ModalBody>
                    }
                    values={blocklist}
                    onRemove={removeFromBlocklist}
                />
                <ModalBody>
                    <span className={styles.note}>
                        You can change this list at any time in your email settings
                    </span>
                </ModalBody>
            </ModalMain>
            <FooterWithControls
                controls={<SlideshowControls selectedSlide={2} slideCount={3} />}
                buttons={
                    <React.Fragment>
                        <Button variant='text' onClick={props.goBack}>
                            Back
                        </Button>
                        <SyncMutations
                            provider={props.provider}
                            fromDaysAgo={props.syncOld ? INITIAL_SYNC_NUM_DAYS_AGO : undefined}
                            createContacts={props.createPeople}
                        >
                            {({startSync}) => (
                                <Button
                                    variant='primary'
                                    size='big'
                                    disabled={isConnecting}
                                    onClick={() => {
                                        setIsConnecting(true);
                                        Promise.all([setBlocklist(blocklist), startSync()])
                                            .then(() => {
                                                // TODO: verify that sync is actually turned on
                                                setIsConnecting(false);
                                                props.onSyncSuccessful();
                                            })
                                            .catch(() => {
                                                setIsConnecting(false);
                                            });
                                    }}
                                >
                                    {isConnecting ? 'Setting up sync…' : 'Finish & start syncing'}
                                </Button>
                            )}
                        </SyncMutations>
                    </React.Fragment>
                }
            />
        </RightFrame>
    );
}

type SetUniqueListTuple = [
    string[],
    (arg1: string) => void,
    (arg1: string) => void,
    (arg1: string[]) => void,
];

function useUniqueList(initialList: string[]): SetUniqueListTuple {
    const [list, setList] = React.useState<string[]>(initialList);

    // @ts-expect-error
    function addToList(item) {
        // Don't allow blank additions
        if (!item) return;

        const newList = Ramda.uniq(list.concat(item));
        setList(newList);
    }

    // @ts-expect-error
    function removeFromList(item) {
        const newList = Ramda.without([item], list);
        setList(newList);
    }

    return [list, addToList, removeFromList, setList];
}
